/**
 * Import Monitor — Queue Search tab.
 *
 * Order Lookup answers a question about one purchase order. This does the same
 * for everything else the middleware sends — parts, stock, forecasts,
 * suppliers, GRNs, currency rates — searching the retained pushes across every
 * Redis queue and grouping the hits by queue.
 *
 * The window is whatever retention holds: the last 7 days, and always the
 * last two pushes per data type (CR-8675).
 * That is not a filter this page applies, it is the whole of what exists,
 * because older logs are deleted as each job completes.
 */
import { useCallback, useState } from 'react';
import { Loader2, Search, Database, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  importsAPI,
  type QueueSearchResponse,
  type QueueSearchQueue,
  type QueueSearchRow,
} from '../../lib/api/imports';

const ACTION_STYLES: Record<string, string> = {
  created: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  updated: 'bg-blue-100 text-blue-800 border-blue-200',
  skipped: 'bg-slate-100 text-slate-700 border-slate-200',
  unallocated: 'bg-amber-100 text-amber-800 border-amber-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
};

/** Naive title-casing turns grns into "Grns" and open_po_details into
 *  "Open Po Details". These are the acronyms that actually occur. */
const ACRONYMS: Record<string, string> = {
  grn: 'GRN', grns: 'GRNs', po: 'PO', pos: 'POs',
  ncr: 'NCR', ncrs: 'NCRs', rfq: 'RFQ', rfqs: 'RFQs',
  otif: 'OTIF', erp: 'ERP', sap: 'SAP', asn: 'ASN', asns: 'ASNs',
};

function title(dataType: string): string {
  return dataType
    .split('_')
    .map(word => ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function fmtWhen(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function MatchRow({ row }: { row: QueueSearchRow }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-0 py-2.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <span className={`px-2 py-0.5 rounded-full border text-xs font-medium shrink-0 ${ACTION_STYLES[row.action] || ACTION_STYLES['skipped']}`}>
            {row.action}
          </span>
          <span className="font-medium font-mono text-xs truncate">{row.entity_reference || '—'}</span>
          <span className="text-xs text-muted-foreground shrink-0">{row.entity_type}</span>
        </div>
        <span className="text-xs text-muted-foreground shrink-0">
          {fmtWhen(row.job_created_at)}{row.source_row ? ` · row ${row.source_row}` : ''}
        </span>
      </div>

      {row.error_message && (
        <p className={`text-xs mt-1 ${row.action === 'failed' ? 'text-red-700' : 'text-muted-foreground'}`}>
          {row.error_code ? <span className="font-mono mr-1">{row.error_code}</span> : null}
          {row.error_message}
        </p>
      )}

      {row.raw_record ? (
        <>
          <button onClick={() => setOpen(!open)} className="mt-1.5 text-xs text-primary hover:underline">
            {open ? 'hide payload as received' : 'view payload as received'}
          </button>
          {open && (
            <pre className="mt-2 p-3 rounded bg-muted text-xs overflow-x-auto">
              {JSON.stringify(row.raw_record, null, 2)}
            </pre>
          )}
        </>
      ) : (
        <p className="mt-1.5 text-xs text-muted-foreground italic">
          Payload not retained for this row (recorded before every row kept its payload).
        </p>
      )}
    </div>
  );
}

function QueueCard({ queue }: { queue: QueueSearchQueue }) {
  const [open, setOpen] = useState(queue.match_count > 0);
  const hasMatches = queue.match_count > 0;

  return (
    <Card className={hasMatches ? '' : 'opacity-70'}>
      <CardContent className="p-4">
        <button
          onClick={() => setOpen(!open)}
          disabled={!hasMatches}
          className="w-full flex items-start justify-between gap-3 text-left disabled:cursor-default"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Database className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-semibold text-sm">{title(queue.data_type)}</span>
              {hasMatches ? (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                  {queue.match_count} match{queue.match_count === 1 ? '' : 'es'}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">no matches</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 font-mono break-all">
              {queue.redis_keys.length > 0 ? queue.redis_keys.join(' · ') : 'no Redis queue configured'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {queue.retained_pushes} retained push{queue.retained_pushes === 1 ? '' : 'es'} ·{' '}
              {queue.retained_rows.toLocaleString()} row{queue.retained_rows === 1 ? '' : 's'}
            </p>
          </div>
          {hasMatches && (
            <span className="text-xs text-primary shrink-0">{open ? 'hide' : 'show'}</span>
          )}
        </button>

        {open && hasMatches && (
          <div className="mt-3 border-t pt-1">
            {queue.matches.map(row => <MatchRow key={row.id} row={row} />)}
            {queue.truncated && (
              <p className="text-xs text-muted-foreground mt-2">
                Showing the first 50 of {queue.match_count} matches on this queue.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Queue search for one customer.
 *
 * @param customerId - Customer whose retained pushes to search
 */
export default function ImportQueueSearchTab({ customerId }: { customerId: number }) {
  const [term, setTerm] = useState('');
  const [deep, setDeep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<QueueSearchResponse | null>(null);

  const search = useCallback(async () => {
    const q = term.trim();
    if (!q || !customerId) return;
    setLoading(true);
    setResult(null);
    try {
      setResult(await importsAPI.getQueueSearch(q, customerId, { deep }));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [term, deep, customerId]);

  if (!customerId) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          Select a customer to search their queues.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Search every middleware queue</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Finds a reference across the retained pushes — the last 7 days, and always
              the last two per data type, which is everything still held. Grouped by queue, with the payload as received.
            </p>
          </div>
          <div className="flex gap-2">
            <input
              id="import-queue-search"
              name="q"
              aria-label="Search term"
              value={term}
              onChange={e => setTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void search(); }}
              placeholder="Part number, GRN, supplier code, PO:line…"
              className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background
                         focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button
              onClick={() => void search()}
              disabled={loading || !term.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground
                         disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              id="import-queue-search-deep"
              name="deep"
              type="checkbox"
              checked={deep}
              onChange={e => setDeep(e.target.checked)}
              className="w-3.5 h-3.5 accent-primary"
            />
            Also search inside the payloads — slower, but finds values that are a field
            in the record rather than its reference.
          </label>
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">{result.total_matches}</strong> match
              {result.total_matches === 1 ? '' : 'es'} for <strong className="text-foreground">{result.q}</strong>
              {result.deep ? ` · scanned ${result.deep_scanned.toLocaleString()} payloads` : ''}
            </p>
          </div>

          {/* A capped payload scan has genuinely not looked everywhere, and the
              rows it skipped are in no meaningful order — so a nil result here
              is not evidence of absence. */}
          {result.deep_truncated && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900">
                The payload scan stopped at {result.deep_scanned.toLocaleString()} rows, so it has
                not looked everywhere. Narrow the search with a more specific term before
                concluding the value is absent.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {result.queues.map(queue => <QueueCard key={queue.data_type} queue={queue} />)}
          </div>
        </>
      )}
    </div>
  );
}
